import { useEffect, useState } from 'react';
import { isStandalone, isIOS } from './platform';

const DISMISSED_KEY = 'installPromptDismissed';

// Wraps the beforeinstallprompt lifecycle (Chrome/Android/desktop) and
// falls back to iOS "Share -> Add to Home Screen" instructions, since
// Safari never fires that event. Only meant to be shown to logged-in
// users, and hidden once the app is already running standalone.
//
// Android's dismiss is remembered (there's a real native prompt to not
// re-nag about). iOS has no native prompt to dismiss -- it's just static
// instructions -- so there's nothing to permanently hide there; the menu
// item stays put and its own X only collapses the expanded steps.
const useInstallPrompt = (enabled) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [androidDismissed, setAndroidDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === 'true'
  );
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const dismissAndroid = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setAndroidDismissed(true);
  };

  const promptInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === 'dismissed') dismissAndroid();
  };

  const canShow = enabled && !installed;

  return {
    canPromptNative: canShow && !androidDismissed && !!deferredPrompt,
    canShowIOSInstructions: canShow && !deferredPrompt && isIOS(),
    promptInstall,
    dismissAndroid,
  };
};

export default useInstallPrompt;
